import {useQueryClient} from '@tanstack/react-query';
import {useCallback, useEffect, useRef, useState} from 'react';

import {abortAllRequests} from '~api/api';
import {AuthService, TOKEN_STORAGE_KEY} from '~api/auth.service';
import {getUser} from '~api/users';
import {reclaimCustomerToken} from '~utils/customerSwitch';
import {orgIdQueryParam} from '~utils/routeUtil';
import {selectCustomer, selectUserId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

export function useReclaimOrgOnFocus(): {isReclaimingOrg: boolean} {
  const customer = useAppSelector(selectCustomer);
  const userId = useAppSelector(selectUserId);
  const queryClient = useQueryClient();
  const [isReclaimingOrg, setIsReclaimingOrg] = useState(false);
  const tokenChangedExternally = useRef(false);
  const isReclaimInFlight = useRef(false);
  const customerRef = useRef(customer);
  customerRef.current = customer;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const reclaimIfNeeded = useCallback(async () => {
    const targetCustomer = customerRef.current;
    if (!tokenChangedExternally.current || isReclaimInFlight.current || !targetCustomer || !AuthService.isLoggedIn()) {
      return;
    }
    isReclaimInFlight.current = true;
    setIsReclaimingOrg(true);
    try {
      await queryClient.cancelQueries();
      // The loop condition re-checks the flag after every await in the body,
      // so a token change from another tab during any of them starts another
      // exchange instead of being lost.
      while (tokenChangedExternally.current) {
        tokenChangedExternally.current = false;
        await reclaimCustomerToken(targetCustomer);
        await abortAllRequests();
        queryClient.removeQueries();
        const freshUser = await getUser();
        if (freshUser.userId !== userIdRef.current) {
          window.location.reload();
          return;
        }
      }
    } catch (error) {
      console.error('Failed to reclaim the org token after an external customer switch:', error);
      const url = new URL(window.location.href);
      url.searchParams.set(orgIdQueryParam, targetCustomer);
      // Same same-origin guard as switchCustomerAndReload, so the reload provably stays on this app.
      window.location.replace(url.origin === window.location.origin ? url.toString() : '/archive');
    } finally {
      isReclaimInFlight.current = false;
      setIsReclaimingOrg(false);
    }
  }, [queryClient]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === TOKEN_STORAGE_KEY && event.newValue !== null) {
        tokenChangedExternally.current = true;
      }
    };
    const handleFocus = () => {
      void reclaimIfNeeded();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void reclaimIfNeeded();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reclaimIfNeeded]);

  return {isReclaimingOrg};
}
